import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaBars, FaChevronDown, FaGraduationCap } from 'react-icons/fa';

const Navbar = ({ consultancyName = "Vaishnavi Educare", bannerShown = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSection, setActiveSection] = useState('home');

  // ponytail: useMemo prevents useEffect dep from changing every render
  const navLinks = useMemo(() => [
    { label: 'Home', href: '#home' },
    { label: 'About', href: '#about' },
    { label: 'Services', href: '#services' },
    { label: 'Colleges', href: '#colleges' },
    { label: 'Study Abroad', href: '#study-abroad' },
    { label: 'NEET Predictor', href: '#neet-predictor' },
    { label: 'Contact', href: '#contact' },
  ], []);

  const educationalPrograms = [
    { label: 'MBBS in India', href: '#mbbs-india' },
    { label: 'MBBS in Nepal', href: '#study-abroad' },
    { label: 'BAMS Colleges', href: '#bams-colleges' },
    { label: 'Engineering', href: '#engineering' },
  ];

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) setScrolled(isScrolled);
    };
    document.addEventListener('scroll', handleScroll);
    return () => document.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-50% 0px -50% 0px',
      threshold: 0,
    };
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActiveSection(entry.target.id);
      });
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    const sections = navLinks.map(link => document.querySelector(link.href)).filter(Boolean);
    sections.forEach(section => observer.observe(section));
    return () => sections.forEach(section => observer.unobserve(section));
  }, [navLinks]);

  const toggleDropdown = (name) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  const NavLink = ({ href, children, hasDropdown, dropdownName }) => {
    const containerRef = useRef(null);
    const isActive = href === `#${activeSection}`;

    return (
      <div
        className="relative group"
        ref={containerRef}
        onMouseEnter={() => hasDropdown && setActiveDropdown(dropdownName)}
        onMouseLeave={() => hasDropdown && setActiveDropdown(null)}
      >
        <a
          href={href}
          className="relative flex items-center px-3 py-2 text-base font-medium cursor-pointer group"
          onClick={() => {
            if (hasDropdown) toggleDropdown(dropdownName);
            else setIsMenuOpen(false);
          }}
        >
          <span className="absolute bottom-0 left-3 right-3 h-px bg-primary-700 scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" />
          <span className={`transition-colors duration-200 ${
            (activeDropdown === dropdownName || isActive)
              ? 'text-primary-800 font-semibold'
              : 'text-gray-700 group-hover:text-primary-800'
          }`}>
            {children}
          </span>
          {hasDropdown && (
            <FaChevronDown className={`ml-1 text-xs transition-transform duration-200 ${
              activeDropdown === dropdownName ? 'rotate-180 text-primary-700' : 'text-gray-500'
            }`} />
          )}
        </a>

        {hasDropdown && (
          <AnimatePresence>
            {activeDropdown === dropdownName && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
                className="absolute z-50 mt-0 min-w-[200px] hidden lg:block"
                onMouseEnter={() => setActiveDropdown(dropdownName)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <div className="py-1 bg-white rounded-b-lg rounded-tr-lg border border-slate-200 border-t-0 shadow-lg">
                  <ul>
                    {educationalPrograms.map((item, index) => (
                      <li key={index}>
                        <a
                          href={item.href}
                          className="block px-4 py-2 text-sm text-gray-700 hover:text-primary-800 hover:bg-primary-50 transition-colors duration-150"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {item.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    );
  };

  // Offset below the announcement banner when it's shown (banner height = 40px = top-10)
  const navTop = bannerShown ? 'top-10' : 'top-0';

  return (
    <nav className={`fixed ${navTop} left-0 right-0 z-50 transition-all duration-200 ${
      scrolled
        ? 'bg-white shadow-sm border-b border-slate-200'
        : 'bg-white/90 backdrop-blur-sm'
    }`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3">
          <a href="/" className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt={`${consultancyName} Logo`}
              className="h-10 w-auto"
            />
            <div className="hidden sm:block">
              <div className="text-xl font-bold font-heading leading-tight">
                <span className="text-primary-800">{consultancyName.split(' ')[0]}</span>
                <span className="text-gray-700 ml-1">{consultancyName.split(' ').slice(1).join(' ')}</span>
              </div>
              <div className="text-xs text-gray-500 tracking-wide">
                Your Trusted Education Consultant
              </div>
            </div>
            <span className="sm:hidden text-lg font-bold font-heading text-primary-800">
              {consultancyName.split(' ').map(word => word[0]).join('')}
            </span>
          </a>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link, index) => {
              if (link.label === 'Services') {
                return (
                  <NavLink key={index} href={link.href} hasDropdown dropdownName="programs">
                    {link.label}
                  </NavLink>
                );
              }
              if (link.label === 'NEET Predictor') {
                return (
                  <a
                    key={index}
                    href={link.href}
                    className="relative flex items-center gap-1.5 px-3 py-1.5 ml-1 rounded-full text-sm font-semibold text-primary-800 bg-primary-50 border border-primary-200 hover:bg-primary-100 transition-colors duration-150"
                  >
                    <FaGraduationCap className="text-xs" />
                    {link.label}
                  </a>
                );
              }
              return <NavLink key={index} href={link.href}>{link.label}</NavLink>;
            })}
            <a
              href="#contact"
              className="ml-3 px-5 py-2 rounded-full bg-primary-800 text-white text-sm font-medium hover:bg-primary-900 transition-colors duration-200"
            >
              Request Counselling
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden text-gray-700 p-2 rounded-lg hover:bg-slate-100 transition-colors duration-150"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="lg:hidden bg-white border-t border-slate-200 shadow-lg"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-4 py-3 space-y-1">
              {navLinks.map((link, index) =>
                link.label === 'Services' ? (
                  <div key={index}>
                    <button
                      className="flex items-center justify-between w-full px-3 py-2 text-base font-medium text-gray-700 rounded-lg hover:bg-slate-50"
                      onClick={() => toggleDropdown('mobilePrograms')}
                    >
                      <span className={activeDropdown === 'mobilePrograms' ? 'text-primary-800 font-semibold' : ''}>
                        {link.label}
                      </span>
                      <FaChevronDown className={`text-xs transition-transform duration-200 ${
                        activeDropdown === 'mobilePrograms' ? 'rotate-180 text-primary-700' : 'text-gray-400'
                      }`} />
                    </button>
                    <AnimatePresence>
                      {activeDropdown === 'mobilePrograms' && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.15 }}
                        >
                          <ul className="pl-4 mt-1 space-y-1 border-l border-slate-200 ml-3">
                            {educationalPrograms.map((item, idx) => (
                              <li key={idx}>
                                <a
                                  href={item.href}
                                  className="block px-3 py-2 text-sm text-gray-600 hover:text-primary-800 transition-colors"
                                  onClick={() => { setIsMenuOpen(false); setActiveDropdown(null); }}
                                >
                                  {item.label}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <a
                    key={index}
                    href={link.href}
                    className="block px-3 py-2 text-base font-medium text-gray-700 rounded-lg hover:bg-slate-50 hover:text-primary-800 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                )
              )}
              <div className="pt-2 pb-1">
                <a
                  href="#contact"
                  className="block w-full text-center py-2.5 px-5 rounded-full bg-primary-800 text-white font-medium hover:bg-primary-900 transition-colors duration-200"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Request Counselling
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
